import { Component, OnInit } from "@angular/core"
import { CommonModule } from "@angular/common"
import { FormsModule } from "@angular/forms"
import { ActivatedRoute, Router } from "@angular/router"
import { MatFormFieldModule } from "@angular/material/form-field"
import { MatSelectModule } from "@angular/material/select"
import { MatIconModule } from "@angular/material/icon"
import { DataLoaderComponent } from "../../../shared/data-loader/data-loader.component"
import { AuthService } from "../../../services/auth.service"
import { ProfileService } from "../../../services/profile.service"
import {
  SupportAttachment,
  SupportCategory,
  ReassignOptions,
  SupportReply,
  SupportService,
  SupportTicket,
} from "../../../services/support.service"

@Component({
  selector: "app-ticket-detail",
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatFormFieldModule,
    MatSelectModule,
    MatIconModule,
    DataLoaderComponent,
  ],
  templateUrl: "./ticket-detail.component.html",
  styleUrl: "./ticket-detail.component.css",
})
export class TicketDetailComponent implements OnInit {
  ticket: SupportTicket | null = null
  replies: SupportReply[] = []
  categories: SupportCategory[] = []
  loading = false
  replyText = ""
  isReplying = false
  updateStatus = ""
  markResolved = false
  showReassignPanel = false
  reassignOptions: ReassignOptions | null = null
  loadingReassignOptions = false
  reassignMode: "station" | "user" = "station"
  selectedStationId: number | null = null
  selectedUserId: number | null = null
  reassignError = ""
  reassignSuccess = ""
  isReassigning = false
  actionError = ""

  replyAttachments: SupportAttachment[] = []
  uploadingReplyFiles = false
  replyUploadError = ""

  readonly ALLOWED_TYPES = [
    "image/png",
    "image/jpeg",
    "image/jpg",
    "image/gif",
    "image/webp",
    "application/pdf",
    "text/plain",
  ]
  readonly MAX_FILE_SIZE = 10 * 1024 * 1024
  readonly MAX_FILES = 5

  constructor(
    private supportService: SupportService,
    private authService: AuthService,
    private profileService: ProfileService,
    private route: ActivatedRoute,
    public router: Router,
  ) {}

  get isStaff(): boolean {
    const role = this.authService.getCurrentUserInfo()?.role?.toLowerCase()
    if (["admin", "owner", "station_owner", "station_user"].includes(role || "")) return true
    // Station staff whose users.role is "user": staff for tickets routed to their station.
    return (
      this.ticket?.routed_to === "station" && this.supportService.isAssignedToStation(this.ticket?.station_id)
    )
  }

  get isAdmin(): boolean {
    return this.authService.getCurrentUserInfo()?.role?.toLowerCase() === "admin"
  }

  get ticketSource(): "advertiser" | "station" {
    return this.ticket ? this.supportService.ticketSource(this.ticket) : "advertiser"
  }

  get ticketSourceLabel(): string {
    return this.ticket ? this.supportService.ticketSourceLabel(this.ticket) : "Advertiser"
  }

  /** Staff-raised tickets stay with RAD admin — they cannot be sent to a station. */
  get canReassign(): boolean {
    return this.isAdmin && this.ticketSource !== "station"
  }

  get assignmentType(): "station" | "admin" | "" {
    const type = this.ticket?.assignment?.current?.type?.toLowerCase()
    if (type === "station") return "station"
    if (type === "admin" || type === "user") return "admin"
    if (this.ticket?.routed_to === "station") return "station"
    if (this.ticket?.routed_to === "admin" && this.ticket.assigned_to) return "admin"
    if (this.ticket?.station_id) return "station"
    return ""
  }

  get assignmentLabel(): string {
    if (!this.ticket) return ""
    const current = this.ticket.assignment?.current
    if (current?.label?.trim()) return current.label.trim()

    const stationName = current?.station_name || this.ticket.station_name || ""
    const callSign = current?.call_sign || this.ticket.call_sign || ""
    const ownerName = current?.owner_name || this.ticket.owner_name || ""
    const assignedName =
      current?.assigned_name ||
      [this.ticket.assignee_firstname, this.ticket.assignee_lastname]
        .filter((part) => !!part?.trim())
        .join(" ")
        .trim()

    if (this.assignmentType === "station" || stationName) {
      const station = stationName
        ? callSign
          ? `${stationName} (${callSign})`
          : stationName
        : ""
      return [station, ownerName || assignedName].filter(Boolean).join(" · ")
    }

    return assignedName
  }

  get reassignedByName(): string {
    const fromAssignment = this.ticket?.assignment?.reassigned_by_name?.trim()
    if (fromAssignment) return fromAssignment
    return [this.ticket?.reassigned_by_firstname, this.ticket?.reassigned_by_lastname]
      .filter((part) => !!part?.trim())
      .join(" ")
      .trim()
  }

  get reassignedAt(): string | null {
    return this.ticket?.assignment?.reassigned_at || this.ticket?.reassigned_at || null
  }

  get hasAssignment(): boolean {
    return !!(this.assignmentLabel || this.reassignedByName || this.reassignedAt)
  }

  ngOnInit(): void {
    this.loadCategories()
    this.route.paramMap.subscribe((params) => {
      const id = Number(params.get("id"))
      if (id) {
        this.loadTicket(id)
      }
    })
  }

  loadCategories(): void {
    this.supportService.getCategories("filter").subscribe({
      next: (categories) => {
        this.categories = categories ?? []
      },
      error: () => {
        this.categories = []
      },
    })
  }

  loadTicket(ticketId?: number): void {
    const id = ticketId ?? this.ticket?.id
    if (!id) return

    this.loading = true
    this.actionError = ""
    this.supportService.getTicket(id).subscribe({
      next: (res) => {
        this.ticket = res?.ticket ?? null
        this.replies = res?.replies ?? []
        this.loading = false
      },
      error: () => {
        this.ticket = null
        this.replies = []
        this.loading = false
        this.actionError = "Unable to load this ticket."
      },
    })
  }

  submitReply(): void {
    if (!this.replyText.trim() || !this.ticket) return

    this.isReplying = true
    this.actionError = ""
    const data: {
      message: string
      update_status?: string
      attachment_ids?: number[]
    } = {
      message: this.replyText.trim(),
    }
    if (this.updateStatus) {
      data.update_status = this.updateStatus
    }
    if (this.replyAttachments.length > 0) {
      data.attachment_ids = this.replyAttachments.map((a) => a.id)
    }

    this.supportService.replyToTicket(this.ticket.id, data).subscribe({
      next: () => {
        this.replyText = ""
        this.markResolved = false
        this.updateStatus = ""
        this.replyAttachments = []
        this.replyUploadError = ""
        this.isReplying = false
        this.loadTicket(this.ticket!.id)
      },
      error: (err) => {
        this.actionError =
          err?.error?.error || err?.error?.message || "Unable to send reply. Please try again."
        this.isReplying = false
      },
    })
  }

  updateTicketStatus(status: string): void {
    if (!status || !this.ticket) return

    this.actionError = ""
    this.supportService.updateTicket(this.ticket.id, { status }).subscribe({
      next: () => {
        this.updateStatus = ""
        this.loadTicket(this.ticket!.id)
      },
      error: (err) => {
        this.actionError =
          err?.error?.error || err?.error?.message || "Unable to update ticket status."
        this.updateStatus = ""
      },
    })
  }

  get isOwnTicket(): boolean {
    if (!this.ticket) return false
    const myId = this.profileService.getUserId()
    if (myId != null && this.ticket.user_id != null) {
      return Number(this.ticket.user_id) === Number(myId)
    }
    const myEmail = (
      this.profileService.getCurrentUser()?.email ||
      this.authService.getCurrentUserInfo()?.email ||
      ""
    ).toLowerCase()
    return !!myEmail && (this.ticket.user_email || "").toLowerCase() === myEmail
  }

  get backLabel(): string {
    if (!this.isStaff || this.isOwnTicket) return "Back to My Tickets"
    return "Back to Inbox"
  }

  goBack(): void {
    if (!this.isStaff || this.isOwnTicket) {
      this.router.navigate(["/help"])
      return
    }
    this.router.navigate(["/help/tickets"])
  }

  getCategoryLabel(category: string): string {
    return this.categories.find((cat) => cat.value === category)?.label || category
  }

  getStatusLabel(status: string): string {
    const labels: Record<string, string> = {
      open: "Open",
      in_progress: "In Progress",
      resolved: "Resolved",
      closed: "Closed",
    }
    return labels[status] || status
  }

  getInitials(first?: string, last?: string): string {
    return `${first?.charAt(0) || ""}${last?.charAt(0) || ""}`.toUpperCase()
  }

  get selectedStationOption() {
    return this.reassignOptions?.stations.find((s) => s.station_id === this.selectedStationId) || null
  }

  onMarkResolvedChange(): void {
    this.updateStatus = this.markResolved ? "resolved" : ""
  }

  openReassignPanel(): void {
    if (!this.canReassign) return
    this.showReassignPanel = true
    this.loadingReassignOptions = true
    this.reassignError = ""
    this.reassignSuccess = ""
    this.prefillReassignFromTicket()

    this.supportService.getReassignOptions().subscribe({
      next: (res) => {
        this.reassignOptions = {
          stations: res?.stations ?? [],
          admins: res?.admins ?? [],
        }
        this.prefillReassignFromTicket()
        this.loadingReassignOptions = false
      },
      error: () => {
        this.loadingReassignOptions = false
        this.reassignError = "Failed to load reassign options."
      },
    })
  }

  private prefillReassignFromTicket(): void {
    const current = this.ticket?.assignment?.current
    const type = this.assignmentType || "station"
    this.reassignMode = type === "admin" ? "user" : "station"

    const stationId = Number(current?.station_id ?? this.ticket?.station_id)
    this.selectedStationId =
      this.reassignMode === "station" && Number.isFinite(stationId) && stationId > 0
        ? stationId
        : null

    const assignedTo = Number(current?.assigned_to ?? this.ticket?.assigned_to)
    this.selectedUserId =
      this.reassignMode === "user" && Number.isFinite(assignedTo) && assignedTo > 0
        ? assignedTo
        : null
  }

  closeReassignPanel(): void {
    this.showReassignPanel = false
    this.selectedStationId = null
    this.selectedUserId = null
    this.reassignError = ""
    this.reassignSuccess = ""
  }

  confirmReassign(): void {
    if (!this.ticket || !this.canReassign) return
    if (this.reassignMode === "station" && !this.selectedStationId) {
      this.reassignError = "Please select a station."
      return
    }
    if (this.reassignMode === "user" && !this.selectedUserId) {
      this.reassignError = "Please select an assignee."
      return
    }

    this.isReassigning = true
    this.reassignError = ""

    const payload: {
      routed_to: "station" | "admin"
      status: "in_progress"
      station_id?: number
      assigned_to?: number
    } = {
      routed_to: this.reassignMode === "station" ? "station" : "admin",
      status: "in_progress",
    }

    if (this.reassignMode === "station" && this.selectedStationId) {
      payload.station_id = this.selectedStationId
      const ownerUserId = Number(this.selectedStationOption?.owner_user_id)
      if (Number.isFinite(ownerUserId) && ownerUserId > 0) {
        payload.assigned_to = ownerUserId
      }
    }

    if (this.reassignMode === "user" && this.selectedUserId) {
      payload.assigned_to = this.selectedUserId
    }

    this.supportService.updateTicket(this.ticket.id, payload).subscribe({
      next: () => {
        this.isReassigning = false
        this.reassignSuccess = "Ticket reassigned successfully. The assignee has been notified."
        setTimeout(() => {
          this.loadTicket()
          this.closeReassignPanel()
        }, 1500)
      },
      error: (err) => {
        this.isReassigning = false
        this.reassignError = err?.error?.error || "Failed to reassign ticket."
      },
    })
  }

  onAttachmentSelected(event: Event): void {
    const input = event.target as HTMLInputElement
    if (!input.files) return
    this.uploadFiles(Array.from(input.files))
    input.value = ""
  }

  onAttachmentDrop(event: DragEvent): void {
    event.preventDefault()
    const files = Array.from(event.dataTransfer?.files || [])
    this.uploadFiles(files)
  }

  removeAttachment(id: number): void {
    this.replyAttachments = this.replyAttachments.filter((a) => a.id !== id)
  }

  isImage(fileType: string): boolean {
    return (fileType || "").startsWith("image/")
  }

  formatFileSize(bytes: number): string {
    if (bytes < 1024) return bytes + " B"
    if (bytes < 1048576) return (bytes / 1024).toFixed(1) + " KB"
    return (bytes / 1048576).toFixed(1) + " MB"
  }

  private uploadFiles(files: File[]): void {
    for (const file of files) {
      if (!this.ALLOWED_TYPES.includes(file.type)) {
        this.replyUploadError = `${file.name}: File type not allowed. Use PNG, JPG, GIF, WebP, PDF, or TXT.`
        continue
      }
      if (file.size > this.MAX_FILE_SIZE) {
        this.replyUploadError = `${file.name}: File too large. Max 10MB.`
        continue
      }
      if (this.replyAttachments.length >= this.MAX_FILES) {
        this.replyUploadError = `Maximum ${this.MAX_FILES} files allowed.`
        break
      }

      this.uploadingReplyFiles = true
      this.supportService.uploadAttachment(file).subscribe({
        next: (res) => {
          if (res?.attachment) {
            this.replyAttachments.push(res.attachment)
          }
          this.uploadingReplyFiles = false
          this.replyUploadError = ""
        },
        error: () => {
          this.uploadingReplyFiles = false
          this.replyUploadError = `Failed to upload ${file.name}.`
        },
      })
    }
  }
}
